import { addMonths, addYears, endOfMonth, endOfYear, startOfMonth, startOfYear, subMonths, subYears } from 'date-fns';
import { EventsFilter } from '../../../Types/Event';

export const GERMAN_MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

export type DateIntervalSelectionValues = {
  year: string;
  month: string;
};

const normalizeMonthValue = (value: string) =>
  value.trim().toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');

export const getMonthNumber = (value: string) => {
  const normalizedValue = normalizeMonthValue(value);

  if (!normalizedValue) {
    return '';
  }

  const monthIndex = GERMAN_MONTHS.findIndex((month) => normalizeMonthValue(month) === normalizedValue);

  if (monthIndex >= 0) {
    return (monthIndex + 1).toString().padStart(2, '0');
  }

  const numericValue = normalizedValue.replace(/\D/g, '').slice(0, 2);
  if (numericValue.length === 2) {
    const monthNumber = Number(numericValue);
    if (monthNumber >= 1 && monthNumber <= 12) {
      return numericValue;
    }
  }

  return '';
};

export const getMonthLabel = (monthValue: string) => {
  if (monthValue.length !== 2) {
    return '';
  }

  const monthIndex = Number(monthValue) - 1;
  return GERMAN_MONTHS[monthIndex] ?? '';
};

export const getCustomIntervalInputValues = (from?: Date, to?: Date): DateIntervalSelectionValues => {
  if (!from || !to) {
    return { year: '', month: '' };
  }

  const fromDate = new Date(from);
  const toDate = new Date(to);
  const year = fromDate.getFullYear();
  const month = fromDate.getMonth() + 1;

  if (
    fromDate.getTime() === startOfMonth(fromDate).getTime() &&
    toDate.getTime() === endOfMonth(toDate).getTime() &&
    year === toDate.getFullYear() &&
    month === toDate.getMonth() + 1
  ) {
    return { year: year.toString(), month: getMonthLabel(month.toString().padStart(2, '0')) };
  }

  if (
    fromDate.getTime() === startOfYear(fromDate).getTime() &&
    toDate.getTime() === endOfYear(toDate).getTime() &&
    year === toDate.getFullYear()
  ) {
    return { year: year.toString(), month: '' };
  }

  return { year: '', month: '' };
};

export const getIntervalInputValues = (
  interval: EventsFilter['interval'],
  from?: Date,
  to?: Date,
): DateIntervalSelectionValues => {
  const customInterval = getCustomIntervalInputValues(from, to);
  if (customInterval.year) {
    return customInterval;
  }

  const today = new Date();

  switch (interval) {
    case 'LAST_MONTH': {
      const targetDate = subMonths(today, 1);
      return {
        year: targetDate.getFullYear().toString(),
        month: getMonthLabel((targetDate.getMonth() + 1).toString().padStart(2, '0')),
      };
    }
    case 'CURRENT_MONTH':
      return {
        year: today.getFullYear().toString(),
        month: getMonthLabel((today.getMonth() + 1).toString().padStart(2, '0')),
      };
    case 'NEXT_MONTH': {
      const targetDate = addMonths(today, 1);
      return {
        year: targetDate.getFullYear().toString(),
        month: getMonthLabel((targetDate.getMonth() + 1).toString().padStart(2, '0')),
      };
    }
    case 'LAST_YEAR':
      return { year: subYears(today, 1).getFullYear().toString(), month: '' };
    case 'CURRENT_YEAR':
      return { year: today.getFullYear().toString(), month: '' };
    case 'FUTURE':
    case 'ALL_TIME':
    default:
      return { year: '', month: '' };
  }
};

export const getDateRangeFromSelection = (yearValue: string, monthValue: string) => {
  if (yearValue.length !== 4) {
    return null;
  }

  const year = Number(yearValue);
  const normalizedMonth = getMonthNumber(monthValue);
  const month = Number(normalizedMonth);
  const from =
    normalizedMonth.length === 2 ? startOfMonth(new Date(year, month - 1, 1)) : startOfYear(new Date(year, 0, 1));
  const to = normalizedMonth.length === 2 ? endOfMonth(from) : endOfYear(from);

  return { from, to };
};
